// Restricted (c) Siemens Healthineers AG, 2025, All rights reserved.

import { Injectable } from '@angular/core';
import { debounceTime, Subject, takeUntil } from 'rxjs';
import { CtsGantryLogFilterModel } from '../model/cts-gantry-log-filter-model';
import { CtsGantryLog } from '../interfaces/cts-gantry-log';
import { CtsGantryLogSeverityType } from '../enum/cts-gantry-log-severity-type';

@Injectable({ providedIn: 'root' })
export class CtsGantryFilterLogService {
  public filter = new CtsGantryLogFilterModel();

  private readonly unsubscribe = new Subject<void>();
  private readonly searchSubject = new Subject<string>();
  private readonly filterSubject = new Subject<CtsGantryLogFilterModel>();
  private readonly filteredLogsSubject = new Subject<CtsGantryLog[]>();

  private logs: CtsGantryLog[] = [];

  public initLogFilterService(
    logs: CtsGantryLog[],
    callback: (logs: CtsGantryLog[]) => void,
  ): void {
    this.filter = new CtsGantryLogFilterModel();
    this.initLogLists(logs);
    this.listenToSubjects();
    this.listenToFilteredLogsSubject(callback);
  }

  private initLogLists(logs: CtsGantryLog[]): void {
    this.logs = logs;
    this.filteredLogsSubject.next(this.logs);
  }

  private listenToFilteredLogsSubject(
    callback: (logs: CtsGantryLog[]) => void,
  ): void {
    this.filteredLogsSubject
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(callback);
  }

  private listenToSubjects(): void {
    this.listenToSearchSubject();
    this.listenToFilterSubject();
  }

  public unsubscribeSubjects(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  public onErrorCheck(value: boolean): void {
    this.filter.errors = value;
    this.filterSubject.next(this.filter);
  }

  public onInfoCheck(value: boolean): void {
    this.filter.informations = value;
    this.filterSubject.next(this.filter);
  }

  public onWarningCheck(value: boolean): void {
    this.filter.warnings = value;
    this.filterSubject.next(this.filter);
  }

  public onInputChange(value: string): void {
    this.filter.searchInput = value;
    this.searchSubject.next(this.filter.searchInput);
  }

  private listenToFilterSubject(): void {
    this.filterSubject
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(this.updateFilteredLogs);
  }

  private listenToSearchSubject(): void {
    this.searchSubject
      .pipe(debounceTime(300), takeUntil(this.unsubscribe))
      .subscribe(this.updateFilteredLogs);
  }

  private readonly updateFilteredLogs = () => {
    const filteredLogs = this.logs
      .filter((log) => this.checkSearchInput(this.filter.searchInput, log))
      .filter(this.filterBySeverity(this.filter));

    this.filteredLogsSubject.next(filteredLogs);
  };

  private readonly filterBySeverity = (
    checkedValues: CtsGantryLogFilterModel,
  ) => {
    const activeStatuses: CtsGantryLogSeverityType[] = [];

    if (checkedValues.errors)
      activeStatuses.push(CtsGantryLogSeverityType.ERROR);
    if (checkedValues.warnings)
      activeStatuses.push(CtsGantryLogSeverityType.WARNING);
    if (checkedValues.informations)
      activeStatuses.push(CtsGantryLogSeverityType.INFORMATION);

    return (log: {
      status: string;
      timestamp: string;
      domainId: string;
      message: string;
      severity: string;
    }) =>
      activeStatuses.length === 0 ||
      activeStatuses.includes(log.severity as CtsGantryLogSeverityType);
  };

  private readonly checkSearchInput = (
    value: string,
    log: {
      status: string;
      timestamp: string;
      domainId: string;
      message: string;
    },
  ) => {
    const localValue = (value ?? '').trim().toLocaleLowerCase();
    const localMsg = log.message.toLocaleLowerCase();
    const localDomainId = log.domainId.toLocaleLowerCase();
    return localMsg.includes(localValue) || localDomainId.includes(localValue);
  };

  public onToggleSort(
    property: keyof CtsGantryLog,
    sortDescending: boolean,
  ): void {
    this.logs.sort((loga, logb) => {
      const a = String(loga[property] ?? '');
      const b = String(logb[property] ?? '');
      return a.localeCompare(b) * (sortDescending ? -1 : 1);
    });

    this.updateFilteredLogs();
  }
}
